import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';

/**
 * @typedef {Object} WishlistItem
 * @property {string} id - The product id.
 * @property {string} handle - The product handle, used to hydrate the wishlist page.
 */

/**
 * Stores and syncs the customer's wishlist in localStorage.
 *
 * Guests get a shared "guest" list. When a customer is logged in the list is namespaced to
 * their customer id so a shared device doesn't mix wishlists between accounts. Any items saved
 * while signed out are merged into the customer's list the first time they're detected as
 * logged in, then the guest list is cleared.
 *
 * Every mutation dispatches `wishlist:updated` on `document` so all UI (card hearts, the header
 * count, the wishlist page) can react instantly without a page reload — mirrors the
 * `StandardEvents.cartLinesUpdate` pattern used for the cart elsewhere in the theme.
 */
export class WishlistStore {
  /** @type {string} */
  static EVENT_NAME = 'wishlist:updated';

  /** @type {string} */
  static #STORAGE_PREFIX = 'takemore:wishlist:';

  /** @type {number} Hard cap so a runaway script (or a very enthusiastic browsing session)
   *  can't grow localStorage without bound. */
  static #MAX_ITEMS = 200;

  /** @type {boolean} */
  static #migrated = false;

  /**
   * @returns {string} The storage namespace for the current visitor.
   */
  static get #namespace() {
    const customerId = typeof Theme !== 'undefined' ? Theme.customer?.id : null;
    return customerId ? `customer-${customerId}` : 'guest';
  }

  /**
   * @param {string} [namespace]
   * @returns {string}
   */
  static #key(namespace = this.#namespace) {
    return `${this.#STORAGE_PREFIX}${namespace}`;
  }

  /**
   * @param {string} [namespace]
   * @returns {WishlistItem[]}
   */
  static #read(namespace = this.#namespace) {
    try {
      const raw = localStorage.getItem(this.#key(namespace));
      const parsed = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(parsed)) return [];

      // Defensively drop malformed entries — localStorage can be edited by hand (devtools,
      // browser extensions, a stale schema from a previous version of this code) and nothing
      // downstream should trust its shape.
      return parsed
        .filter(
          (item) =>
            item && typeof item === 'object' && typeof item.id !== 'undefined' && typeof item.handle === 'string'
        )
        .map((item) => ({ id: String(item.id), handle: item.handle }))
        .slice(0, this.#MAX_ITEMS);
    } catch (error) {
      console.warn('[wishlist] Failed to read from localStorage:', error);
      return [];
    }
  }

  /**
   * @param {WishlistItem[]} items
   * @param {string} [namespace]
   */
  static #write(items, namespace = this.#namespace) {
    try {
      localStorage.setItem(this.#key(namespace), JSON.stringify(items.slice(0, this.#MAX_ITEMS)));
    } catch (error) {
      console.warn('[wishlist] Failed to write to localStorage:', error);
    }
  }

  /**
   * Merges any guest-session wishlist into the signed-in customer's wishlist, once per page load.
   */
  static #migrateGuestToCustomer() {
    if (this.#migrated) return;
    this.#migrated = true;

    const customerId = typeof Theme !== 'undefined' ? Theme.customer?.id : null;
    if (!customerId) return;

    const guestItems = this.#read('guest');
    if (guestItems.length === 0) return;

    const customerItems = this.#read(`customer-${customerId}`);
    const merged = [...customerItems];
    for (const item of guestItems) {
      if (!merged.some((existing) => String(existing.id) === String(item.id))) {
        merged.push(item);
      }
    }

    this.#write(merged, `customer-${customerId}`);
    this.#write([], 'guest');
  }

  /**
   * @returns {WishlistItem[]} All wishlisted items for the current visitor.
   */
  static getAll() {
    this.#migrateGuestToCustomer();
    return this.#read();
  }

  /**
   * @returns {number}
   */
  static count() {
    return this.getAll().length;
  }

  /**
   * @param {string | number} id
   * @returns {boolean}
   */
  static has(id) {
    return this.getAll().some((item) => String(item.id) === String(id));
  }

  /**
   * Adds an item if it isn't already present. No-ops (and does not dispatch) on duplicates.
   * @param {WishlistItem} item
   */
  static add(item) {
    const items = this.getAll();
    if (items.some((existing) => String(existing.id) === String(item.id))) return;
    if (items.length >= this.#MAX_ITEMS) {
      console.warn(`[wishlist] Limit of ${this.#MAX_ITEMS} items reached.`);
      return;
    }

    items.push({ id: String(item.id), handle: item.handle });
    this.#write(items);
    this.#dispatchUpdate();
  }

  /**
   * @param {string | number} id
   */
  static remove(id) {
    const items = this.getAll();
    const next = items.filter((item) => String(item.id) !== String(id));
    if (next.length === items.length) return;

    this.#write(next);
    this.#dispatchUpdate();
  }

  /**
   * Toggles an item's presence in the wishlist.
   * @param {WishlistItem} item
   * @returns {boolean} Whether the item is now saved.
   */
  static toggle(item) {
    if (this.has(item.id)) {
      this.remove(item.id);
      return false;
    }

    this.add(item);
    return true;
  }

  static #dispatchUpdate() {
    const items = this.#read();
    document.dispatchEvent(
      new CustomEvent(this.EVENT_NAME, {
        bubbles: true,
        detail: { ids: items.map((item) => item.id), count: items.length },
      })
    );
  }
}

/**
 * `<wishlist-button>` — wraps a real `<button ref="button">` heart toggle so keyboard/screen
 * reader semantics come for free. Used on product cards and the product page.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} button
 *
 * @extends {Component<Refs>}
 */
class WishlistButton extends Component {
  requiredRefs = ['button'];

  /** @type {AbortController} */
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    this.#syncState();

    document.addEventListener(WishlistStore.EVENT_NAME, this.#syncState, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @returns {string}
   */
  get #productId() {
    return this.dataset.productId ?? '';
  }

  /**
   * @returns {string}
   */
  get #productHandle() {
    return this.dataset.productHandle ?? '';
  }

  #syncState = () => {
    if (!this.#productId) return;

    const isActive = WishlistStore.has(this.#productId);
    this.refs.button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    this.classList.toggle('wishlist-button--active', isActive);

    const label = isActive ? Theme.translations.remove_from_wishlist : Theme.translations.add_to_wishlist;
    if (label) this.refs.button.setAttribute('aria-label', label);
  };

  /**
   * Handles the button click — toggles the wishlist state, no network request, no reload.
   * @param {Event} event
   */
  toggle = (event) => {
    event.preventDefault();

    if (!this.#productId) return;

    const isNowActive = WishlistStore.toggle({ id: this.#productId, handle: this.#productHandle });
    if (isNowActive) this.#playPop();
  };

  /**
   * Brief "pop" played only when saving (not when removing) — a quick, purposeful confirmation
   * that the click registered, cleaned up automatically once the animation finishes.
   */
  #playPop = async () => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.refs.button.classList.add('wishlist-button__control--pop');
    await onAnimationEnd(this.refs.button);
    this.refs.button.classList.remove('wishlist-button__control--pop');
  };
}

if (!customElements.get('wishlist-button')) {
  customElements.define('wishlist-button', WishlistButton);
}

/**
 * `<wishlist-icon>` — the header action icon, mirrors `<cart-icon>` (assets/cart-icon.js) but
 * reads its count straight from `WishlistStore` since localStorage is the source of truth here
 * (unlike the cart, there's no server round-trip to reconcile after a bfcache restore).
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wishlistBubble
 * @property {HTMLElement} wishlistBubbleText
 * @property {HTMLElement} wishlistBubbleCount
 *
 * @extends {Component<Refs>}
 */
class WishlistIcon extends Component {
  requiredRefs = ['wishlistBubble', 'wishlistBubbleText', 'wishlistBubbleCount'];

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {number} */
  #previousCount = 0;

  connectedCallback() {
    super.connectedCallback();
    // Seed the baseline so the bubble doesn't play its "grow" animation on first paint
    // when a returning visitor already has saved items.
    this.#previousCount = WishlistStore.count();
    this.#render();

    document.addEventListener(WishlistStore.EVENT_NAME, this.#render, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  #render = async () => {
    const count = WishlistStore.count();
    const increased = count > this.#previousCount;
    this.#previousCount = count;

    this.refs.wishlistBubbleCount.textContent = count > 0 ? String(count) : '';
    this.refs.wishlistBubbleCount.classList.toggle('hidden', count === 0);
    this.refs.wishlistBubble.classList.toggle('visually-hidden', count === 0);

    if (!increased || count === 0 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    // Reuses the cart bubble's existing "grow" / "move-and-fade" keyframes (see
    // sections/header.liquid) — same feedback pattern as adding to cart, no new CSS needed.
    await new Promise((resolve) => requestAnimationFrame(resolve));
    this.refs.wishlistBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.wishlistBubbleText);
    this.refs.wishlistBubble.classList.remove('cart-bubble--animating');
  };
}

if (!customElements.get('wishlist-icon')) {
  customElements.define('wishlist-icon', WishlistIcon);
}
